// src/pages/teams/TeamInviteManager.jsx
'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  createInvite,
  listTeamInvites,
  revokeInvite,
  reactivateByClone,
  buildInviteUrl,
  mailtoForInvite,
} from '@/services/inviteService';
import {
  getTeam,
  onTeamMembers,
  updateTeamMemberRole,
  removeTeamMember,
  getUserByUid,
} from '@/services/teamService';
import { useTeamAccess } from '@/hooks/useTeamAccess';
import {
  canInviteStaff,
  canChangeStaffRole,
  canRemoveStaff,
} from '@/constants/roles';
import {
  ChevronLeftIcon,
  DocumentDuplicateIcon,
  ShareIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  UserMinusIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import ParentInviteCard from '@/components/invite/ParentInviteCard';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

function formatDate(ts) {
  if (!ts) return 'N/A';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' }).format(d);
  } catch {
    return 'N/A';
  }
}

function statusChip(status) {
  const s = (status || '').toLowerCase();
  const base = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide border';
  switch (s) {
    case 'active':
      return { label: 'Active', className: `${base} bg-[#b5f03d]/15 border-[#b5f03d]/40 text-[#3d6010]` };
    case 'expired':
      return { label: 'Expired', className: `${base} bg-amber-50 border-amber-200 text-amber-700` };
    case 'accepted':
      return { label: 'Used', className: `${base} bg-sky-50 border-sky-200 text-sky-700` };
    case 'revoked':
      return { label: 'Revoked', className: `${base} bg-zinc-100 border-zinc-200 text-zinc-600` };
    default:
      return { label: status || 'Unknown', className: `${base} bg-zinc-100 border-zinc-200 text-zinc-600` };
  }
}

function getInviteTargetLabel(inv) {
  if (inv?.metadata?.label) return inv.metadata.label;
  if (inv?.email) return inv.email;
  if (inv?.to) return inv.to;
  return 'General Invite';
}

const ASSIGNABLE_ROLES = [
  { value: 'coach',     label: 'Coach' },
  { value: 'manager',   label: 'Manager' },
  { value: 'assistant', label: 'Assistant' },
];

function roleLabel(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'owner') return 'Owner';
  if (r === 'team_manager') return 'Manager (legacy)';
  const found = ASSIGNABLE_ROLES.find(x => x.value === r);
  return found ? found.label : (r ? r.charAt(0).toUpperCase() + r.slice(1) : 'Member');
}

function memberDisplayName(member, userDoc) {
  return (
    member.displayName ||
    member.name ||
    userDoc?.displayName ||
    userDoc?.name ||
    member.email ||
    userDoc?.email ||
    userDoc?.emailLower ||
    'Unknown'
  );
}

function memberEmail(member, userDoc) {
  return member.email || userDoc?.email || userDoc?.emailLower || '';
}

function SectionLabel({ children }) {
  return (
    <p
      className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
      style={{ fontFamily: HEAD_FONT }}
    >
      {children}
    </p>
  );
}

function PrimaryButton({ children, disabled, onClick, className = '', type = 'button' }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 min-h-[48px] uppercase transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 14, letterSpacing: '0.6px' }}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, onClick, className = '', as: As = 'button', ...rest }) {
  return (
    <As
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-3.5 py-2 text-[13px] font-semibold text-[#0d1f14] transition-colors ${className}`}
      {...rest}
    >
      {children}
    </As>
  );
}

function DangerButton({ children, onClick, disabled, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 hover:border-rose-300 px-3.5 py-2 text-[13px] font-semibold text-rose-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}

export default function TeamInviteManager() {
  const { user } = useAuth();
  const { teamId } = useParams();
  const [listLoading, setListLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteLabel, setInviteLabel] = useState('');
  const [revealMap, setRevealMap] = useState({});
  const [showRevoked, setShowRevoked] = useState(false);

  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [userDocs, setUserDocs] = useState({});
  const [savingRole, setSavingRole] = useState({});

  const HARDCODED_EXPIRY_DAYS = 14;

  const { access } = useTeamAccess(teamId, { clubId: team?.clubId ?? null });

  const canManage = canInviteStaff(access);
  const canEditRole = canChangeStaffRole(access);
  const canRemove = canRemoveStaff(access);

  const refresh = useCallback(async () => {
    if (!teamId) return;
    setListLoading(true);
    try {
      const data = await listTeamInvites(teamId);
      setInvites(data || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load invites');
    } finally {
      setListLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    if (!teamId) return;
    getTeam(teamId)
      .then((t) => { if (!cancelled) setTeam(t); })
      .catch((e) => console.warn('[TeamInviteManager] getTeam failed', e?.message));
    return () => { cancelled = true; };
  }, [teamId]);

  useEffect(() => {
    if (!teamId) return;
    setMembersLoading(true);
    const unsub = onTeamMembers(teamId, (list) => {
      setMembers(list);
      setMembersLoading(false);
    });
    return () => { try { unsub && unsub(); } catch {} };
  }, [teamId]);

  useEffect(() => {
    const missing = members.filter(
      (m) => !m.displayName && !m.name && !m.email && !userDocs[m.id]
    );
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const pairs = await Promise.all(
        missing.map(async (m) => {
          try {
            const u = await getUserByUid(m.id);
            return [m.id, u || null];
          } catch {
            return [m.id, null];
          }
        })
      );
      if (cancelled) return;
      setUserDocs((prev) => {
        const next = { ...prev };
        for (const [id, u] of pairs) next[id] = u;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [members, userDocs]);

  async function onChangeRole(memberId, newRole) {
    if (!canEditRole) return toast.error('You don\u2019t have permission to change roles');
    if ((memberId === team?.ownerId || memberId === team?.coachId)) return toast.error("Can't change the owner's role here");
    setSavingRole((s) => ({ ...s, [memberId]: true }));
    try {
      await updateTeamMemberRole(teamId, memberId, newRole);
      toast.success('Role updated');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to update role');
    } finally {
      setSavingRole((s) => {
        const next = { ...s };
        delete next[memberId];
        return next;
      });
    }
  }

  async function onRemoveMember(memberId) {
    if (!canRemove) return toast.error('You don\u2019t have permission to remove members');
    if ((memberId === team?.ownerId || memberId === team?.coachId)) return toast.error("Can't remove the team owner");
    const member = members.find((m) => m.id === memberId);
    const name = memberDisplayName(member || {}, userDocs[memberId]);
    const ok = window.confirm(`Remove ${name} from this team? They will lose access immediately.`);
    if (!ok) return;
    try {
      await removeTeamMember(teamId, memberId);
      toast.success('Member removed');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to remove member');
    }
  }

  async function onCreate() {
    if (!user?.uid) return toast.error('Not signed in');
    if (!teamId) return toast.error('Missing team');

    const finalLabel = inviteLabel.trim() || 'General Manager Invite';

    setCreating(true);
    const toastId = toast.loading('Generating secure link...');
    try {
      const { code } = await createInvite({
        teamId,
        teamName: team?.name || null,
        audience: 'staff',
        preset: 'manager',
        createdBy: user.uid,
        email: undefined,
        expiresInDays: HARDCODED_EXPIRY_DAYS,
        metadata: {
          label: finalLabel,
          createdVia: 'web-manager-dashboard',
        },
      });

      setInviteLabel('');
      await refresh();

      const link = buildInviteUrl(code, teamId);
      await navigator.clipboard.writeText(link);
      toast.success('Link copied to clipboard', { id: toastId });
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Could not create invite', { id: toastId });
    } finally {
      setCreating(false);
    }
  }

  async function onRevoke(inviteCode) {
    const ok = window.confirm('Revoke this invite? The link will stop working immediately.');
    if (!ok) return;
    try {
      await revokeInvite(inviteCode);
      toast.success('Invite revoked');
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to revoke invite');
    }
  }

  async function onResend(inviteId) {
    if (!user?.uid) return toast.error('Not signed in');
    try {
      const { code: newCode } = await reactivateByClone({
        teamId,
        inviteId,
        createdBy: user.uid,
      });
      toast.success('Invite re-activated');
      const link = buildInviteUrl(newCode, teamId);
      await navigator.clipboard.writeText(link);
      toast('New link copied');
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to resend invite');
    }
  }

  function toggleReveal(id) {
    setRevealMap((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  const sortedInvites = [...invites].sort((a, b) => {
    const statusA = (a.status || 'active').toLowerCase();
    const statusB = (b.status || 'active').toLowerCase();
    const getScore = (s) => (s === 'active' ? 2 : s === 'revoked' ? 0 : 1);
    const scoreDiff = getScore(statusB) - getScore(statusA);
    if (scoreDiff !== 0) return scoreDiff;
    return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
  });

  const activeInvites = sortedInvites.filter(i => (i.status || 'active').toLowerCase() === 'active');
  const inactiveInvites = sortedInvites.filter(i => (i.status || 'active').toLowerCase() !== 'active');
  const visibleRevoked = showRevoked ? inactiveInvites.filter(i => i.status === 'revoked') : [];
  const visibleExpired = inactiveInvites.filter(i => i.status !== 'revoked');

  function InviteRow({ inv }) {
    const link = buildInviteUrl(inv.code, teamId);
    const chip = statusChip(inv.status);
    const revealed = !!revealMap[inv.id];
    const targetLabel = getInviteTargetLabel(inv);
    const isActive = (inv.status || 'active').toLowerCase() === 'active';
    const legacyEmail = inv?.email || inv?.metadata?.email;

    return (
      <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] transition-all hover:border-[#b5f03d]/40">
        <div className="p-4 sm:p-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#b5f03d]/15">
                <span
                  className="text-sm text-[#3d6010] uppercase"
                  style={{ fontFamily: HEAD_FONT, fontWeight: 800 }}
                >
                  {targetLabel.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3
                    className="text-[#0d1f14] truncate"
                    style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 16, letterSpacing: '0.3px' }}
                  >
                    {targetLabel}
                  </h3>
                  <span className={chip.className}>{chip.label}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Created {formatDate(inv.createdAt)}
                </div>
              </div>
            </div>

            <div className="mt-3 max-w-2xl rounded-lg bg-zinc-50 border border-zinc-200 p-2 flex items-center gap-3">
              <code className="flex-1 truncate font-mono text-xs text-zinc-700">
                {revealed ? link : `${link.substring(0, 30)}•••••••••••••`}
              </code>
              <button
                type="button"
                onClick={() => toggleReveal(inv.id)}
                className="shrink-0 inline-flex items-center justify-center h-7 w-7 rounded text-zinc-500 hover:text-[#0d1f14] hover:bg-white transition-colors"
                aria-label={revealed ? 'Hide link' : 'Reveal link'}
              >
                {revealed ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center lg:pl-4">
            <GhostButton
              className="w-full sm:w-auto"
              onClick={async () => {
                await navigator.clipboard.writeText(link);
                toast.success('Copied');
              }}
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
              Copy
            </GhostButton>

            {typeof navigator !== 'undefined' && 'share' in navigator && (
              <GhostButton
                className="w-full sm:w-auto"
                onClick={() => navigator.share({ title: 'Join Team', url: link })}
              >
                <ShareIcon className="h-4 w-4" />
                Share
              </GhostButton>
            )}

            {legacyEmail && (
              <GhostButton
                as="a"
                className="w-full sm:w-auto"
                href={mailtoForInvite({ link, to: legacyEmail })}
              >
                <EnvelopeIcon className="h-4 w-4" />
                Email
              </GhostButton>
            )}

            {canManage && (isActive ? (
              <DangerButton
                className="w-full sm:w-auto"
                onClick={() => onRevoke(inv.code)}
              >
                Revoke
              </DangerButton>
            ) : (
              <GhostButton
                className="w-full sm:w-auto"
                onClick={() => onResend(inv.id)}
              >
                <ArrowPathIcon className="h-4 w-4" />
                Resend
              </GhostButton>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function MemberRow({ member }) {
    const userDoc = userDocs[member.id];
    const name = memberDisplayName(member, userDoc);
    const email = memberEmail(member, userDoc);
    const role = String(member.role || 'member').toLowerCase();
    const isThisOwner = role === 'owner'
      || member.id === team?.ownerId
      || member.id === team?.coachId;
    const isYou = member.id === user?.uid;
    const saving = !!savingRole[member.id];
    const legacyManager = role === 'team_manager';

    const initial = (name || '?').trim().charAt(0).toUpperCase();

    return (
      <div className="rounded-xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] transition-all hover:border-[#b5f03d]/40">
        <div className="p-4 sm:p-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#b5f03d]/15">
              <span
                className="text-sm text-[#3d6010] uppercase"
                style={{ fontFamily: HEAD_FONT, fontWeight: 800 }}
              >
                {initial}
              </span>
              {isThisOwner && (
                <span
                  className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-white ring-2 ring-white"
                  title="Team owner"
                >
                  <ShieldCheckIcon className="h-2.5 w-2.5" />
                </span>
              )}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3
                  className="text-[#0d1f14] truncate"
                  style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 16, letterSpacing: '0.3px' }}
                >
                  {name}
                </h3>
                {isYou && (
                  <span className="inline-flex items-center rounded-full bg-sky-100 border border-sky-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-700">
                    You
                  </span>
                )}
                {isThisOwner ? (
                  <span className="inline-flex items-center rounded-full bg-amber-100 border border-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                    Owner
                  </span>
                ) : legacyManager ? (
                  <span className="inline-flex items-center rounded-full bg-zinc-100 border border-zinc-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-zinc-700">
                    {roleLabel(role)}
                  </span>
                ) : null}
              </div>
              {email && (
                <div className="text-xs text-zinc-500 mt-0.5 truncate">{email}</div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center lg:pl-4">
            {isThisOwner ? (
              <p className="text-xs text-zinc-500 italic sm:pr-2">
                Ownership transfer not available here
              </p>
            ) : canEditRole ? (
              <>
                <select
                  value={ASSIGNABLE_ROLES.find(r => r.value === role)
                    ? role
                    : (role === 'team_manager' ? 'manager' : 'assistant')}
                  onChange={(e) => onChangeRole(member.id, e.target.value)}
                  disabled={saving}
                  className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:border-[#b5f03d] focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/30 disabled:opacity-50"
                  aria-label={`Role for ${name}`}
                >
                  {ASSIGNABLE_ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <DangerButton
                  className="w-full sm:w-auto"
                  onClick={() => onRemoveMember(member.id)}
                  disabled={saving}
                >
                  <UserMinusIcon className="h-4 w-4" />
                  Remove
                </DangerButton>
              </>
            ) : (
              <span className="text-xs text-zinc-500 px-2 py-1 rounded-md bg-zinc-50 border border-zinc-200">
                {roleLabel(role)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  const sortedMembers = [...members].sort((a, b) => {
    const rank = (m) => {
      const r = String(m.role || 'member').toLowerCase();
      if (r === 'owner' || m.id === team?.ownerId || m.id === team?.coachId) return 0;
      if (r === 'coach') return 1;
      if (r === 'manager' || r === 'team_manager') return 2;
      if (r === 'assistant') return 3;
      if (r === 'member') return 4;
      return 5;
    };
    const diff = rank(a) - rank(b);
    if (diff !== 0) return diff;
    const an = memberDisplayName(a, userDocs[a.id]).toLowerCase();
    const bn = memberDisplayName(b, userDocs[b.id]).toLowerCase();
    return an.localeCompare(bn);
  });

  return (
    <div
      className="min-h-screen bg-[#f7faf5] text-[#0d1f14]"
      style={{ fontFamily: "'Barlow', sans-serif" }}
    >
      <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8 space-y-8">

        {/* Header */}
        <div>
          <Link
            to={`/teams/${teamId}`}
            className="mb-4 inline-flex items-center text-sm font-medium text-zinc-600 hover:text-[#0d1f14] transition-colors"
          >
            <ChevronLeftIcon className="mr-1 h-4 w-4" />
            Back to team
          </Link>
          <div className="flex items-start justify-between gap-3">
            <div>
              <SectionLabel>Team access</SectionLabel>
              <h1
                className="uppercase text-[#0d1f14] leading-[1.05] mt-1"
                style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 32, letterSpacing: '0.3px' }}
              >
                Manager access
              </h1>
              <p className="mt-1 text-zinc-600 text-[15px]">
                Create and manage access links for your staff.
              </p>
            </div>
            <button
              type="button"
              onClick={refresh}
              className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-zinc-200 bg-white text-zinc-600 hover:text-[#0d1f14] hover:border-[#b5f03d]/40 transition-colors"
              aria-label="Refresh"
            >
              <ArrowPathIcon className={`h-4 w-4 ${listLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Parent invite link (reusable, year-long) */}
        <ParentInviteCard
          teamId={teamId}
          uid={user?.uid}
          canManage={canManage}
          teamName={team?.name || ''}
        />

        {/* Create staff invite */}
        {canManage && (
          <section className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
            <div className="border-b border-zinc-200 bg-[#b5f03d]/[0.08] px-5 py-3">
              <SectionLabel>Generate new staff link</SectionLabel>
            </div>
            <div className="grid gap-6 lg:grid-cols-3 p-5 lg:p-6">
              <div className="lg:col-span-1">
                <h3
                  className="text-[#0d1f14] uppercase"
                  style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 18, letterSpacing: '0.3px' }}
                >
                  Invite a coach or manager
                </h3>
                <p className="text-[13px] text-zinc-600 mt-1">
                  Create a unique link for a new coach or manager. Links expire automatically in {HARDCODED_EXPIRY_DAYS} days.
                </p>
              </div>

              <div className="lg:col-span-2 flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="flex-1">
                  <input
                    value={inviteLabel}
                    onChange={(e) => setInviteLabel(e.target.value)}
                    placeholder="Recipient name (e.g. Coach Mike)"
                    className="w-full min-h-[48px] rounded-[10px] border border-zinc-300 bg-white px-4 py-3 text-[14px] text-[#0d1f14] placeholder-zinc-400 focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 focus:outline-none"
                    autoComplete="off"
                  />
                </div>
                <PrimaryButton
                  onClick={onCreate}
                  disabled={creating}
                  className="w-full sm:w-auto whitespace-nowrap"
                >
                  {creating ? 'Creating…' : 'Create invite'}
                </PrimaryButton>
              </div>
            </div>
          </section>
        )}

        {/* Invites list */}
        <section className="space-y-6">
          <SectionLabel>Staff invites</SectionLabel>
          {listLoading ? (
            <div className="space-y-4">
              <div className="h-32 w-full rounded-xl bg-zinc-100 animate-pulse" />
              <div className="h-32 w-full rounded-xl bg-zinc-100 animate-pulse" />
            </div>
          ) : invites.length === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50/50 p-12 text-center">
              <p className="text-zinc-500">No active invites yet.</p>
            </div>
          ) : (
            <>
              {activeInvites.length > 0 && (
                <div className="space-y-3">
                  <h4
                    className="text-[11px] uppercase tracking-[1.4px] text-[#5a8a1f]"
                    style={{ fontFamily: HEAD_FONT, fontWeight: 700 }}
                  >
                    Active
                  </h4>
                  {activeInvites.map(inv => <InviteRow key={inv.id} inv={inv} />)}
                </div>
              )}

              {visibleExpired.length > 0 && (
                <div className="space-y-3">
                  <h4
                    className="text-[11px] uppercase tracking-[1.4px] text-zinc-500"
                    style={{ fontFamily: HEAD_FONT, fontWeight: 700 }}
                  >
                    Past
                  </h4>
                  {visibleExpired.map(inv => <InviteRow key={inv.id} inv={inv} />)}
                </div>
              )}

              <div className="pt-4 border-t border-zinc-200">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-zinc-500">
                    {invites.filter(i => i.status === 'revoked').length} revoked invites hidden
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowRevoked(!showRevoked)}
                    className="text-xs font-semibold text-[#5a8a1f] hover:text-[#3d6010] transition-colors"
                  >
                    {showRevoked ? 'Hide revoked' : 'Show revoked'}
                  </button>
                </div>

                {showRevoked && visibleRevoked.length > 0 && (
                  <div className="mt-4 space-y-3 opacity-75">
                    {visibleRevoked.map(inv => <InviteRow key={inv.id} inv={inv} />)}
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        {/* Current members */}
        <section className="space-y-4">
          <div>
            <SectionLabel>Current members</SectionLabel>
            <p className="mt-1 text-zinc-600 text-[14px]">
              {canEditRole
                ? 'Change a member\u2019s role or remove their access. Staff roles (coach, manager, assistant) can broadcast to the roster.'
                : 'Only the head coach, a club director, or Scrimly staff can change roles or remove members.'}
            </p>
          </div>

          {membersLoading ? (
            <div className="space-y-3">
              <div className="h-20 w-full rounded-xl bg-zinc-100 animate-pulse" />
              <div className="h-20 w-full rounded-xl bg-zinc-100 animate-pulse" />
            </div>
          ) : sortedMembers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50/50 p-12 text-center">
              <p className="text-zinc-500">No members yet. Share an invite above to add staff.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedMembers.map((m) => <MemberRow key={m.id} member={m} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
